/* istanbul ignore file */
import { get, pick, set } from "lodash";
import type { FilterOption } from "../../context/types";

export type OptionLabelType = "selectOption" | "tagOption";

export type FilterToggleConfig = {
  id: string;
  label: string;
  valueKey: string;
};

export type FilterConfig = {
  name: string;
  valueKeys: string[];
  optionsKey?: string;
  itemKey?: string;
  isMulti: boolean;
  isRemoteSearch: boolean;
  inputId: string;
  label: string;
  formatOptionLabel?: string;
  optionLabelType?: OptionLabelType;
  toggles?: FilterToggleConfig[];
  groupedOptions?: { label: string; key: string }[];
  groupingKey?: string;
  className?: string;
};

export type TaggedFilterOption = FilterOption & {
  valueKey?: string | undefined;
};

export type GroupedFilterOption = {
  label: string;
  value: string;
  valueKey: string | undefined;
  options: TaggedFilterOption[];
};

export type FilterItemsMap = Record<string, FilterOption[] | undefined>;

export const FILTER_CONFIGS: {
  COMPANY: FilterConfig;
  LOCATION: FilterConfig;
  COUNTRY: FilterConfig;
  JOBS: FilterConfig;
  TEAM_TYPES: FilterConfig;
  TEAMS: FilterConfig;
  PERSON_TAGS: FilterConfig;
  PERSON_TAGS_AND_TAG_TYPES: FilterConfig;
  ALLOCATION_STATUS: FilterConfig;
} = {
  COMPANY: {
    name: "company",
    valueKeys: ["company"],
    optionsKey: "companies",
    isMulti: true,
    isRemoteSearch: true,
    inputId: "filterCompany",
    label: "Company",
  },
  LOCATION: {
    name: "location",
    valueKeys: ["location"],
    optionsKey: "locations",
    isMulti: true,
    isRemoteSearch: true,
    inputId: "filterLocation",
    label: "Location",
  },
  COUNTRY: {
    name: "country",
    valueKeys: ["country"],
    optionsKey: "countries",
    isMulti: true,
    isRemoteSearch: false,
    inputId: "filterCountry",
    label: "Country",
  },
  JOBS: {
    name: "jobTitle",
    valueKeys: ["jobTitle"],
    optionsKey: "jobs",
    isMulti: true,
    isRemoteSearch: true,
    inputId: "filterJob",
    label: "Role",
  },
  TEAM_TYPES: {
    name: "teams.types",
    valueKeys: ["teams.types"],
    optionsKey: "teamTypes",
    isMulti: true,
    isRemoteSearch: false,
    inputId: "filterTeamType",
    label: "Team Type",
  },
  TEAMS: {
    name: "teams",
    valueKeys: ["teams.teams"],
    isMulti: true,
    isRemoteSearch: true,
    inputId: "filterTeam",
    label: "Team",
    toggles: [
      {
        id: "filterIncludesSubTeams",
        label: "Include sub-teams",
        valueKey: "teams.includeSubTeams",
      },
    ],
  },
  PERSON_TAGS: {
    name: "personTags",
    valueKeys: ["personTags"],
    itemKey: "personTags",
    isMulti: true,
    isRemoteSearch: true,
    inputId: "filterPersonTags",
    label: "People Tags",
    optionLabelType: "tagOption",
  },
  PERSON_TAGS_AND_TAG_TYPES: {
    name: "personTags",
    groupedOptions: [
      {
        label: "Tag Types",
        key: "personTagTypes",
      },
      {
        label: "Tag Values",
        key: "personTags",
      },
    ],
    groupingKey: "personTagTypes",
    itemKey: "personTags",
    valueKeys: ["personTagTypes", "personTags"],
    isMulti: true,
    isRemoteSearch: true,
    optionLabelType: "tagOption",
    inputId: "filterPersonTagsAndTagTypes",
    label: "People Tags",
    className: "peopleTagsSelect",
  },
  ALLOCATION_STATUS: {
    name: "allocationStatus",
    valueKeys: ["allocationStatus"],
    isMulti: true,
    isRemoteSearch: false,
    inputId: "filterAllocationStatus",
    label: "Allocation Status",
  },
};

export const ALLOCATION_STATUS_OPTIONS: FilterOption[] = [
  { label: "Unallocated", value: "unallocated", optionalLabel: null },
  { label: "Under allocated", value: "underAllocated", optionalLabel: null },
  { label: "Over allocated", value: "overAllocated", optionalLabel: null },
];

export const buildOptions = (
  filterItems: FilterItemsMap,
  filterConfig: FilterConfig,
  filters?: Record<string, unknown>
): FilterOption[] | GroupedFilterOption[] => {
  let options: FilterOption[] = get(
    filterItems,
    filterConfig.optionsKey || filterConfig.name,
    []
  );

  if (filterConfig.groupedOptions) {
    const selectedOptionGroupItems = get(
      filters,
      filterConfig.groupingKey || "",
      []
    ) as { value: unknown }[];
    const selectedOptionGroups: unknown[] = selectedOptionGroupItems.map(
      (group) => group.value
    );

    const items: FilterOption[] = get(
      filterItems,
      filterConfig.itemKey || "",
      []
    );
    const sortedItems = items.reduce<Record<string, TaggedFilterOption[]>>(
      (acc, tag) => {
        const type = tag.optionalLabel || "Uncategorised";
        acc[type] = acc[type] ?? [];
        acc[type]?.push({ ...tag, valueKey: filterConfig.itemKey });
        return acc;
      },
      {}
    );

    const optionGroups: FilterOption[] = get(
      filterItems,
      filterConfig.groupingKey || "",
      []
    );
    return optionGroups
      .filter((item) =>
        selectedOptionGroups ? !selectedOptionGroups.includes(item.value) : true
      )
      .map(({ label, value }) => ({
        label,
        value,
        valueKey: filterConfig.groupingKey,
        options: sortedItems[label] || [],
      }));
  }
  return options;
};

export const updateFilterValues = (
  filters: Record<string, unknown>,
  name: string,
  valueKeys: string[],
  values: unknown
): Record<string, unknown> => {
  // Handle nested keys e.g. teams.*
  // name.split(".") always has at least one element, but noUncheckedIndexedAccess
  // types index access as possibly undefined; `?? name` is a typesafe no-op fallback.
  const filterBase = pick(filters, name.split(".")[0] ?? name);
  // filters with multiple valueKeys e.g personTags
  if (Array.isArray(values)) {
    const updates: Record<string, unknown[]> = Object.fromEntries(
      valueKeys.map((k) => [k, []])
    );
    values.forEach((value) => {
      const targetKey = value?.valueKey || valueKeys[0];
      updates[targetKey]?.push(value);
    });
    Object.entries(updates).forEach(([updateKey, updateValues]) => {
      set(filterBase, updateKey, updateValues);
    });
  } else {
    // Handle toggle
    const toggleValue = values as { valueKey: string; value: unknown };
    set(filterBase, toggleValue.valueKey, toggleValue.value);
  }

  return filterBase;
};
